import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import ReactMarkdown from "react-markdown";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { Gavel } from "@mui/icons-material";

const DARK = 'var(--lm-bg-1a1f2b)';
const BORDER = 'rgba(var(--th-6c4f9b-rgb), 0.4)';

// Terms text lives at public/content/tos.md (fetched at runtime, not bundled) so it
// can be edited/redeployed without touching this component. Bump
// AuthController#CURRENT_TOS_VERSION whenever the text changes so users get re-prompted.
// Exported so other components (e.g. AboutModal) can reference the same current version.
export const TOS_URL = '/content/DropWyrmVTT-Terms-of-Use_1.0.md';

const headingStyle = { color: 'var(--lm-tx-f5f5f5)', marginTop: 16, marginBottom: 8 };
const markdownComponents = {
    h1: ({ node, ...props }) => <h5 style={headingStyle} {...props} />,
    h2: ({ node, ...props }) => <h6 style={headingStyle} {...props} />,
    h3: ({ node, ...props }) => <h6 style={{ ...headingStyle, fontSize: '0.9rem' }} {...props} />,
    a: ({ node, ...props }) => <a style={{ color: 'var(--lm-tx-th-c9a0f0)' }} {...props} />,
};

export default function TermsOfServiceModal({ show, onAccept, onDecline, busy }) {
    const [checked, setChecked] = useState(false);
    const [tosText, setTosText] = useState('');

    useEffect(() => {
        if (!show) return;
        fetch(TOS_URL)
            .then(res => res.ok ? res.text() : Promise.reject(res))
            .then(setTosText)
            .catch(() => setTosText('Unable to load the Terms of Service. Please refresh and try again.'));
    }, [show]);

    const handleDecline = () => {
        setChecked(false);
        onDecline();
    };

    return (
        <Modal show={show} centered size="lg" data-bs-theme="dark" enforceFocus={false} backdrop="static" keyboard={false}>
            <Modal.Header style={{ backgroundColor: DARK, borderBottom: `1px solid ${BORDER}` }}>
                <Gavel style={{ height: 24, width: 24, marginRight: 8, color: 'var(--lm-tx-th-c9a0f0)' }} />
                <Modal.Title style={{ color: 'var(--lm-tx-f5f5f5)', fontSize: '1rem' }}>Terms of Service</Modal.Title>
            </Modal.Header>
            <Modal.Body
                className="tos-markdown"
                style={{ maxHeight: '55vh', overflowY: 'auto', color: 'var(--lm-tx-th-e4e0ec)', fontSize: '0.85rem', lineHeight: 1.6 }}
            >
                <ReactMarkdown components={markdownComponents}>{tosText}</ReactMarkdown>
            </Modal.Body>
            <Modal.Footer style={{ backgroundColor: DARK, borderTop: `1px solid ${BORDER}`, flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={checked}
                            onChange={e => setChecked(e.target.checked)}
                            disabled={busy}
                            sx={{ color: 'var(--lm-tx-9e9e9e)', '&.Mui-checked': { color: 'var(--lm-tx-th-c9a0f0)' } }}
                        />
                    }
                    label={<span style={{ color: 'var(--lm-tx-f5f5f5)', fontSize: '0.82rem' }}>I have read and agree to the Terms of Service.</span>}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={handleDecline} disabled={busy} sx={{ color: 'var(--lm-tx-f5f5f5)', textTransform: 'none' }}>
                        Log Out
                    </Button>
                    <Button
                        variant="contained" disabled={!checked || busy} onClick={onAccept}
                        sx={{ backgroundColor: 'var(--th-6c4f9b)', textTransform: 'none', '&:hover': { backgroundColor: 'var(--th-7c5fab)' } }}
                    >
                        {busy ? 'Accepting…' : 'Agree & Continue'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
